import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, FileText, Lock, MessageSquare, Pencil, Shield } from 'lucide-react';
import { API_BASE } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { Card } from './ui/card';
import { getIconComponent } from './IconPicker';
import { handleModifierAwareNavigate } from '../utils/app-navigation';
import { CardMetaText } from './CardMetaText';

type SharePermission = 'reader' | 'commenter' | 'editor' | 'admin';

interface SharedDocument {
  id: string;
  projectId: string | null;
  title: string;
  icon: string | null;
  updatedAt: string;
}

interface SharedProject {
  id: string;
  title: string;
  updatedAt: string;
}

interface SharedDocumentItem {
  document: SharedDocument;
  share: { permission: Exclude<SharePermission, 'admin'> };
  ownerEmail: string | null;
  projectName: string | null;
  sharedViaProjectName?: string | null;
  sharedViaProjectId?: string | null;
}

interface SharedProjectItem {
  project: SharedProject;
  share: { permission: SharePermission };
  ownerEmail: string | null;
  mainDocumentIcon: string | null;
}

interface SharedItemCardProps {
  title: string;
  iconName: string | null;
  permission: SharePermission;
  metaParts: Array<string | false | null | undefined>;
  href: string;
}

const permissionIcon = (permission: SharePermission): React.ReactNode => {
  if (permission === 'reader') return <Lock className="h-4 w-4" style={{ color: '#6b7280', flexShrink: 0 }} />;
  if (permission === 'commenter') return <MessageSquare className="h-4 w-4" style={{ color: '#6b7280', flexShrink: 0 }} />;
  if (permission === 'editor') return <Pencil className="h-4 w-4" style={{ color: '#6b7280', flexShrink: 0 }} />;
  return <Shield className="h-4 w-4" style={{ color: '#6b7280', flexShrink: 0 }} />;
};

const SharedItemCard: React.FC<SharedItemCardProps> = ({ title, iconName, permission, metaParts, href }) => {
  const navigate = useNavigate();
  const meta = metaParts.filter(Boolean).join(' · ');
  const IconComponent = iconName ? getIconComponent(iconName) : null;

  return (
    <Card
      className="relative border-0 shadow-none rounded-[10px] cursor-pointer transition-all hover:bg-card overflow-hidden min-w-0"
      style={{ border: '1px solid var(--border)' }}
      onClick={(e) => {
        handleModifierAwareNavigate(e, navigate, href);
      }}
      onAuxClick={(e) => {
        handleModifierAwareNavigate(e, navigate, href);
      }}
    >
      <div style={{ padding: '1rem', display: 'flex', alignItems: 'stretch', minWidth: 0, overflow: 'hidden' }}>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            textDecoration: 'none',
            color: 'inherit',
            display: 'flex',
            alignItems: 'stretch',
            gap: '0.75rem',
            overflow: 'hidden',
          }}
        >
          {IconComponent ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--sidebar-foreground)',
                minWidth: '48px',
                maxWidth: '48px',
                flexShrink: 0,
                alignSelf: 'stretch',
                height: '100%',
              }}
              className="document-icon-container"
            >
              <IconComponent size={48} />
            </div>
          ) : null}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden' }}>
            <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', lineHeight: '1.5', display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
              {permissionIcon(permission)}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }} title={title}>{title}</span>
            </h3>
            <CardMetaText className="truncate" title={meta}>
              {meta}
            </CardMetaText>
          </div>
        </div>
      </div>
    </Card>
  );
};

export const SharedWithMe: React.FC = () => {
  const { token } = useAuth();
  const { t } = useLocale();
  const [sharedDocuments, setSharedDocuments] = useState<SharedDocumentItem[]>([]);
  const [sharedProjects, setSharedProjects] = useState<SharedProjectItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchSharedItems = async (): Promise<void> => {
      setLoading(true);
      try {
        const [documentsResponse, projectsResponse] = await Promise.all([
          fetch(`${API_BASE}/api/shared-documents`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE}/api/shared-projects`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (!documentsResponse.ok || !projectsResponse.ok) {
          throw new Error('Failed to fetch shared items');
        }

        const [documentsData, projectsData] = await Promise.all([
          documentsResponse.json(),
          projectsResponse.json(),
        ]);

        if (!cancelled) {
          setSharedDocuments(documentsData.documents || []);
          setSharedProjects(projectsData.projects || []);
        }
      } catch (err) {
        console.error('Error fetching shared items:', err);
        if (!cancelled) {
          setSharedDocuments([]);
          setSharedProjects([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSharedItems();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const permissionLabel = (permission: SharePermission): string => {
    if (permission === 'reader') return t('permission_reader') || 'Reader';
    if (permission === 'commenter') return t('permission_commenter') || 'Commenter';
    if (permission === 'editor') return t('permission_editor') || 'Editor';
    return t('permission_admin') || 'Admin';
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
        <div>{t('loading')}</div>
      </div>
    );
  }

  if (sharedProjects.length === 0 && sharedDocuments.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
        <p style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
          {t('no_shared_items') || 'Nothing shared with you yet'}
        </p>
        <p style={{ fontSize: '0.875rem' }}>{t('no_shared_items_desc') || 'Projects and documents shared with you will appear here'}</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
      <div>
        <h2 style={{ margin: '0 0 1rem', fontSize: '1.25rem', fontWeight: 600, color: 'var(--foreground)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FolderOpen className="h-5 w-5" />
          {t('shared_projects') || 'Projects'}
        </h2>
        {sharedProjects.length === 0 ? (
          <div style={{ color: 'var(--muted-foreground)', fontSize: '0.875rem' }}>
            {t('no_shared_projects') || 'No projects shared with you'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {sharedProjects.map(({ project, share, ownerEmail, mainDocumentIcon }) => (
              <SharedItemCard
                key={project.id}
                title={project.title}
                iconName={mainDocumentIcon}
                permission={share.permission}
                href={`/projects/${project.id}`}
                metaParts={[
                  permissionLabel(share.permission),
                  ownerEmail,
                  new Date(project.updatedAt).toLocaleDateString(),
                ]}
              />
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 style={{ margin: '0 0 1rem', fontSize: '1.25rem', fontWeight: 600, color: 'var(--foreground)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FileText className="h-5 w-5" />
          {t('shared_documents') || 'Documents'}
        </h2>
        {sharedDocuments.length === 0 ? (
          <div style={{ color: 'var(--muted-foreground)', fontSize: '0.875rem' }}>
            {t('no_shared_documents') || 'No documents shared with you'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {sharedDocuments.map(({ document, share, ownerEmail, projectName, sharedViaProjectName, sharedViaProjectId }) => {
              const contextProjectId = sharedViaProjectId || document.projectId;
              return (
              <SharedItemCard
                key={document.id}
                title={document.title}
                iconName={document.icon}
                permission={share.permission}
                href={contextProjectId
                  ? `/projects/${contextProjectId}/documents/${document.id}`
                  : `/documents/${document.id}`}
                metaParts={[
                  permissionLabel(share.permission),
                  sharedViaProjectName
                    ? (t('shared_via_project') || 'via {project}').replace('{project}', sharedViaProjectName)
                    : null,
                  sharedViaProjectId ? null : projectName,
                  ownerEmail,
                  new Date(document.updatedAt).toLocaleDateString(),
                ]}
              />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
